import React, { useState, useEffect, useCallback } from 'react';
import { ayarAPI } from '../api/api';
import { waNumara, YER_TUTUCULAR, sablonDoldur, dosyaDegerleri } from '../utils/whatsapp';
import './Ayarlar.css';
import { onayla } from '../utils/onayla';
import { YENI_TASARIM, onizlemeAcikMi, onizlemeyiAyarla } from '../utils/yeniTasarim';

/**
 * Sistem ayarları. Şu an yalnızca WhatsApp bölümü var.
 *
 * WhatsApp NASIL ÇALIŞIR: Sistem mesajı kendisi göndermez; şablonu
 * dosyanın bilgileriyle doldurup WhatsApp'ı hazır mesajla açar, gönder
 * tuşuna kullanıcı basar. Bunun için kayıt, onay ya da ücret gerekmez ve
 * numara normal WhatsApp uygulamasında kullanılmaya devam eder.
 *
 * Numara boş bırakılırsa WhatsApp düğmeleri hiçbir yerde görünmez —
 * yarım kurulmuş bir özellik yanlışlıkla kullanılmasın diye.
 */

/** Şablon önizlemesinde kullanılan örnek dosya. */
const ORNEK_DOSYA = {
  Muvekkil: 'AHMET YILMAZ',
  FoyNo: 220,
  DavaTuru: 20,
  MahkemaBilgisi: 'Ankara 5. İş Mahkemesi',
  MahkemeEsas: '2026/1234',
  DurusmaTarihi: '2026-10-03T00:00:00',
};

const Ayarlar = () => {
  const [numara, setNumara] = useState('');
  const [sablonlar, setSablonlar] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [hata, setHata] = useState('');
  const [bilgi, setBilgi] = useState('');
  const [acikSablon, setAcikSablon] = useState(null);

  /* Gelen Postalar: otomatik kontrol açık mı ve hangi aralıkla.
     Bu ayarlar veritabanında duruyordu ama hiçbir ekranda görünmüyordu. */
  const [postaOtomatik, setPostaOtomatik] = useState(true);
  const [postaAralik, setPostaAralik] = useState(15);

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    setHata('');
    try {
      const v = await ayarAPI.whatsappGetir();
      setNumara(v.numara ?? v.Numara ?? '');
      try {
        const p = await ayarAPI.postaGetir();
        setPostaOtomatik((p.otomatik ?? p.Otomatik) !== false);
        setPostaAralik(Number(p.aralikDakika ?? p.AralikDakika) || 15);
      } catch { /* posta ayarı okunamazsa varsayılanlar kalır */ }
      setSablonlar((v.sablonlar ?? v.Sablonlar ?? []).map((s) => ({
        ad: s.ad ?? s.Ad ?? '',
        metin: s.metin ?? s.Metin ?? '',
      })));
    } catch (e) {
      setHata(e.response?.data?.message || e.message || 'Ayarlar yüklenemedi.');
    } finally {
      setYukleniyor(false);
    }
  }, []);

  useEffect(() => { yukle(); }, [yukle]);

  const kaydet = async () => {
    setKaydediliyor(true);
    setHata('');
    setBilgi('');
    try {
      await ayarAPI.whatsappKaydet({
        Numara: numara || null,
        Sablonlar: sablonlar.map((s) => ({ Ad: s.ad, Metin: s.metin })),
      });
      await ayarAPI.postaKaydet({
        Otomatik: postaOtomatik,
        AralikDakika: Number(postaAralik) || 15,
      });
      setBilgi('Kaydedildi.');
      await yukle();
      window.setTimeout(() => setBilgi(''), 4000);
    } catch (e) {
      setHata(e.response?.data?.message || e.message || 'Kaydedilemedi.');
    } finally {
      setKaydediliyor(false);
    }
  };

  const sablonDegistir = (i, alan, deger) => {
    setSablonlar((ö) => ö.map((s, j) => (j === i ? { ...s, [alan]: deger } : s)));
  };

  const sablonSil = async (i) => {
    if (!await onayla((sablonlar[i].ad || 'Bu şablon') + ' silinsin mi?')) return;
    setSablonlar((ö) => ö.filter((_, j) => j !== i));
    setAcikSablon(null);
  };

  const sablonEkle = () => {
    setSablonlar((ö) => [...ö, { ad: 'Yeni şablon', metin: 'Sayın {muvekkil}, ' }]);
    setAcikSablon(sablonlar.length);
  };

  const duzgunNumara = waNumara(numara);
  const ornekDegerler = dosyaDegerleri(ORNEK_DOSYA);

  if (yukleniyor) return <div className="ay-durum">Ayarlar yükleniyor…</div>;

  return (
    <div className="ay">
      <div className="ay-basi">
        <h1>Ayarlar</h1>
        <p>Sistem genelinde geçerli ayarlar. Yalnızca siz görebilirsiniz.</p>
      </div>

      {/* YENI TASARIM ONIZLEMESI: yalnizca bu cihazda yeni telefon tasarimini
          acar (cerez + IIS "Onizleme" kurali). Diger kullanicilar etkilenmez,
          yayin degildir. */}
      <section className="ay-bolum">
        <header className="ay-bolum-basi">
          <h2>Önizleme: onay bekleyen yenilikler</h2>
          <span className={'ay-rozet ' + (YENI_TASARIM || onizlemeAcikMi() ? 'acik' : 'kapali')}>
            {YENI_TASARIM ? 'önizlemedesiniz' : 'yayında değil'}
          </span>
        </header>
        <p className="ay-aciklama">
          Yayınlanmamış yeni telefon tasarımını yalnızca bu cihazda açar. Diğer
          kullanıcılar eski görünümde kalır; veriler aynıdır. Aynı yerden kapatabilirsiniz.
        </p>
        <button type="button" className="btn btn-primary" onClick={() => onizlemeyiAyarla(!YENI_TASARIM)}>
          {YENI_TASARIM ? 'Eski görünüme dön' : 'Yeni tasarımı bu cihazda dene'}
        </button>
      </section>

      <section className="ay-bolum">
        <header className="ay-bolum-basi">
          <h2>Gelen Postalar</h2>
          <span className={'ay-rozet ' + (postaOtomatik ? 'acik' : 'kapali')}>
            {postaOtomatik ? 'otomatik' : 'elle'}
          </span>
        </header>

        <p className="ay-aciklama">
          Posta kutunuz arka planda kendiliğinden kontrol edilir; sayfaya girip
          "oku" demeniz gerekmez. Yeni posta geldiğinde menüdeki sayı ve zil
          ikonundaki bildirim size haber verir.
        </p>

        <label className="ay-onay-satir">
          <input
            type="checkbox"
            checked={postaOtomatik}
            onChange={(e) => setPostaOtomatik(e.target.checked)}
          />
          <span>
            <b>Otomatik kontrol açık</b>
            <small>Kapatırsanız postalar yalnızca "Şimdi Kontrol Et" ile gelir.</small>
          </span>
        </label>

        <div className="ay-alan">
          <label htmlFor="posta-aralik">Kontrol aralığı</label>
          <select
            id="posta-aralik"
            className="form-control"
            value={postaAralik}
            onChange={(e) => setPostaAralik(Number(e.target.value))}
            disabled={!postaOtomatik}
          >
            {[5, 10, 15, 30, 60, 120, 240].map((d) => (
              <option key={d} value={d}>
                {d < 60 ? d + ' dakikada bir' : (d / 60) + ' saatte bir'}
              </option>
            ))}
          </select>
          <span className="ay-ipucu">
            Sık kontrol posta sunucusunu yorar; 15 dakika çoğu büro için yeterlidir.
          </span>
        </div>
      </section>

      <section className="ay-bolum">
        <header className="ay-bolum-basi">
          <h2>WhatsApp</h2>
          <span className={'ay-rozet ' + (duzgunNumara ? 'acik' : 'kapali')}>
            {duzgunNumara ? 'etkin' : 'numara girilmedi'}
          </span>
        </header>

        <p className="ay-aciklama">
          Numara girildiğinde dosya detayında <b>"WhatsApp'tan bilgi ver"</b> düğmesi
          ve müvekkil uygulamasında WhatsApp ikonu görünür. Sistem mesajı kendisi
          göndermez; şablonu dosyanın bilgileriyle doldurup WhatsApp'ı hazır mesajla
          açar, <b>gönder tuşuna siz basarsınız</b>.
        </p>

        <div className="ay-alan">
          <label htmlFor="wa-numara">Büro WhatsApp numarası</label>
          <input
            id="wa-numara"
            type="tel"
            className="form-control"
            value={numara}
            onChange={(e) => setNumara(e.target.value)}
            placeholder="0532 123 45 67"
            autoComplete="off"
          />
          <span className="ay-ipucu">
            {numara.trim() === ''
              ? 'Boş bırakırsanız WhatsApp özelliği kapalı kalır.'
              : duzgunNumara
                ? 'WhatsApp biçimi: ' + duzgunNumara
                : 'Bu numara tanınmadı. Örnek: 0532 123 45 67'}
          </span>
        </div>

        <div className="ay-not">
          <p>
            <b>Numara henüz WhatsApp'ta aktif olmasa da girebilirsiniz.</b> Düğmeler
            görünür olur ama numara WhatsApp'a kayıtlı olmadan mesaj gönderilemez.
            Numarayı WhatsApp Business uygulamasına kaydettikten sonra her şey
            kendiliğinden çalışır.
          </p>
        </div>

        <h3 className="ay-alt-baslik">Mesaj şablonları</h3>
        <p className="ay-aciklama">
          Şablonun içindeki süslü parantezli alanlar gönderim anında dosyanın
          bilgileriyle doldurulur. Bilgisi olmayan alan olduğu gibi kalır.
        </p>

        <div className="ay-tutucular">
          {YER_TUTUCULAR.map(([k, a]) => (
            <span key={k} className="ay-tutucu" title={a}>
              <code>{k}</code>{a}
            </span>
          ))}
        </div>

        <ul className="ay-sablonlar">
          {sablonlar.map((s, i) => {
            const acik = acikSablon === i;
            return (
              <li key={i} className={acik ? 'acik' : ''}>
                <button
                  type="button"
                  className="ay-sablon-basi"
                  onClick={() => setAcikSablon(acik ? null : i)}
                >
                  <span className="ay-sablon-ad">{s.ad || '(adsız)'}</span>
                  <span className="ay-sablon-ozet">
                    {s.metin.replace(/\s+/g, ' ').slice(0, 60)}
                    {s.metin.length > 60 ? '…' : ''}
                  </span>
                  <span className="ay-ok">{acik ? '−' : '+'}</span>
                </button>

                {acik && (
                  <div className="ay-sablon-govde">
                    <label>
                      Şablon adı
                      <input
                        type="text"
                        className="form-control"
                        value={s.ad}
                        onChange={(e) => sablonDegistir(i, 'ad', e.target.value)}
                      />
                    </label>
                    <label>
                      Mesaj
                      <textarea
                        className="form-control"
                        rows={6}
                        value={s.metin}
                        onChange={(e) => sablonDegistir(i, 'metin', e.target.value)}
                      />
                    </label>

                    <div className="ay-onizleme">
                      <span className="ay-onizleme-baslik">Örnek dosyayla önizleme</span>
                      <pre>{sablonDoldur(s.metin, ornekDegerler)}</pre>
                    </div>

                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => sablonSil(i)}
                    >
                      Şablonu sil
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        <button type="button" className="ay-ekle" onClick={sablonEkle}>
          + Şablon Ekle
        </button>

        {hata && <p className="ay-hata">{hata}</p>}
        {bilgi && <p className="ay-bilgi">{bilgi}</p>}

        <div className="ay-dugmeler">
          <button
            type="button"
            className="btn btn-primary"
            onClick={kaydet}
            disabled={kaydediliyor}
          >
            {kaydediliyor ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </div>
      </section>
    </div>
  );
};

export default Ayarlar;
